import React from 'react';
import { StyleSheet } from 'react-native';

import { mapStyles } from './mapStyles';

const MAP_TYPES = {
  STANDARD: 'standard',
  SATELLITE: 'satellite',
  HYBRID: 'hybrid',
  TERRAIN: 'terrain',
  NONE: 'none',
  MUTEDSTANDARD: 'mutedStandard',
};

/**
 * The web implementation from `react-native-web-maps` does not implement the full MapView API from 'react-native-maps'
 * and does not return the lat/lng deltas in the updated region passed to the onRegionChangeComplete callback. This
 * function grabs the bounds from the underlying GoogleMap implementation and uses it to calculate the deltas and merge
 * them into the region data.
 *
 * The react-native-web-maps library attempts to provide an RNMapView for web that honours the API provided by React
 * Native on mobile.  Unfortunately, react-native-web-maps does not support the `regionDelta` properties in the same way
 * (most likely because it is not supported by the provider they use under the hood: Google Maps).  Although deltas can
 * be provided as props, they are immediately converted the "zoom level" used by Google Maps to define the viewable
 * area, and discarded; and unlike in the React Native map implementations the detals are also not provided in callbacks
 * when the region being viewed by the user changes (in response the user panning around the map, for example).  The
 * absence of the deltas in the callbacks means that we can't track user-initiated changes to the viewable area to - for
 * example - load new provider data.
 *
 * This component instead aims to preserve the "zoom level" used by Google Maps to consistently represent a region,
 * while hiding that as an  implementaion from the rest of the Plentiful app.
 *
 * see: https://github.com/react-native-web-community/react-native-web-maps
 */
export const MapViewWeb = (props) => {
  const { initialRegion, onRegionChangeComplete, onPress, children, mapViewRef, mapViewImpl } = props;

  if (!mapViewImpl) {
    throw new Error('MapViewWeb requires mapViewImpl prop to be provided');
  }

  const mapViewImplRef = React.useRef(null);

  React.useImperativeHandle(
    mapViewRef,
    () => {
      // Wrap the MapView that is passed in as a ref to constrain the interface available to the parent container to the
      // platform-agnositc abstraction that this component provides.
      //
      // https://react.dev/reference/react/useImperativeHandle
      return {
        animateToRegion(mapViewRegion) {
          mapViewImplRef.current?.animateCamera({
            zoom: mapViewRegion.zoom,
            center: { lat: mapViewRegion.latitude, lng: mapViewRegion.longitude },
          });
        },
      };
    },
    []
  );

  const _onRegionChangeComplete = (region) => {
    const r = new MapViewWebRegion(
      region.latitude,
      null,
      region.longitude,
      null,
      mapViewImplRef.current.map.getZoom()
    );
    r.mapImpl = mapViewImplRef.current.map;
    onRegionChangeComplete(r);
  };

  const MapViewImpl = mapViewImpl;

  const component = (
    <MapViewImpl
      defaultZoom={initialRegion.zoom}
      initialRegion={{
        latitude: initialRegion.latitude,
        longitude: initialRegion.longitude,
      }}
      mapType={MAP_TYPES.STANDARD}
      onPress={onPress}
      onRegionChangeComplete={_onRegionChangeComplete}
      options={{
        // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
        styles: mapStyles,
        disableDefaultUI: true,
        zoomControl: true,
      }}
      ref={mapViewImplRef}
      showsUserLocation={false}
      style={styles.mapStyle}
      toolbarEnabled={false}
    >
      {children}
    </MapViewImpl>
  );

  return component;
};

export class MapViewWebRegion {
  constructor(latitude, latitudeDelta, longitude, longitudeDelta, __zoom) {
    this.latitude = latitude;
    this.latitudeDelta = latitudeDelta;
    this.longitude = longitude;
    this.longitudeDelta = longitudeDelta;

    this.mapImpl = null;
    this.zoom = __zoom || this.#regionDeltaToZoomLevel(longitudeDelta);

    this.identity = [this.latitude, this.longitude, this.zoom].join('|');
  }

  #regionDeltaToZoomLevel(delta) {
    return Math.round(Math.log(360 / delta) / Math.LN2) + 1;
  }

  #zoomLevelToRegionDelta(zoomLevel) {
    return 360 / Math.exp(zoomLevel * Math.LN2) + 1;
  }

  asNativeRegion() {
    const centre = {
      latitude: this.latitude,
      longitude: this.longitude,
    };

    if (this.mapImpl) {
      const mapBounds = this.mapImpl.getBounds();
      const boundingNorthEast = mapBounds.getNorthEast();
      const boundingSouthWest = mapBounds.getSouthWest();

      const north = boundingNorthEast.lat();
      const south = boundingSouthWest.lat();
      const east = boundingNorthEast.lng();
      const west = boundingSouthWest.lng();

      const deltaEnrichedRegion = {
        ...centre,

        latitudeDelta: Math.abs(north - south) / 2,
        longitudeDelta: Math.abs(west - east) / 2,
        // Because a zoom level is coarser than deltas, converting from a zoom level to deltas always returns the lowest
        // deltas needed to achieve a zoom level, not the deltas that were originally used to determine the zoom level.,
      };

      return deltaEnrichedRegion;
    } else {
      const delta = this.#zoomLevelToRegionDelta(this.zoom);
      return {
        ...centre,
        latitudeDelta: delta,
        longitudeDelta: delta,
      };
    }
  }

  center(latitude, longitude) {
    return new MapViewWebRegion(latitude, this.latitudeDelta, longitude, this.longitudeDelta, this.zoom);
  }
}

const styles = StyleSheet.create({
  mapStyle: {
    flex: 1,
  },
  locationCross: {
    width: 56,
    height: 56,
    position: 'absolute',
    bottom: 36,
    left: 16,
  },
});
